import { jsx as _jsx, jsxs as _jsxs } from "preact/jsx-runtime";
import { useState, useEffect, useCallback, useRef, useImperativeHandle } from 'preact/hooks';
import { forwardRef } from 'preact/compat';
import { Mail } from 'lucide-preact';
import { TextBox, Select } from '@/components';
import { BRAND_CLASSES } from '@/constants/theme';
import { UnlayerDesigner } from './UnlayerDesigner';
import { ResetCopyToTemplateButton } from './ResetCopyToTemplateButton';
import { SaveStatusIndicator } from '@/components/common/SaveStatusIndicator';
import { assetApi } from '@/api/asset';
function serializeForCompare(value) {
    if (value == null)
        return '';
    try {
        return JSON.stringify(value);
    }
    catch {
        return '';
    }
}
// Merge tag pills exposed in the Email Copy editor's Unlayer UI. Copies own their own
// design (cloned from the bound template at binding time), so they do NOT expose the
// template-only slot markers ({{BODY}} / {{CTA_TEXT}} / {{CTA_URL}}). Only recipient
// personalisation and web-entity variables — these pass through the design at render
// time and are substituted server-side by the variable contract (or filled by the
// future per-recipient send service, in the case of {{first_name}}).
const COPY_MERGE_TAGS = {
    first_name: {
        name: 'Recipient First Name',
        value: '{{first_name}}',
        sample: 'Sarah',
    },
    event_title: {
        name: 'Event Title',
        value: '{{event_title}}',
        sample: 'Reclaiming Source-of-Funds Reviews',
    },
    event_join_url: {
        name: 'Event Join URL',
        value: '{{event_join_url}}',
        sample: 'https://example.com/join/abc123',
    },
    speaker_1_name: {
        name: 'Speaker Name',
        value: '{{speaker_1_name}}',
        sample: 'Alex Chen',
    },
};
/**
 * Email Copy editor panel — Subject input, Template selector, and the shared Unlayer
 * designer seeded from the copy's own DraftDesign. The copy owns its full Unlayer
 * design (cloned from the bound template when the binding was first set on the server
 * side), so the user can freely change alignment, add images, split paragraphs, etc.,
 * without restructuring the template.
 *
 * Autosave fires on subject blur, template change, and (debounced) on every Unlayer
 * design update — persisting DraftDesign + DraftHtml together.
 */
export const EmailCopyEditorPanel = forwardRef(function EmailCopyEditorPanel({ clientCompanyId, workspaceId, campaignId, webId, asset, editable = true, onSaved, }, ref) {
    const [subject, setSubject] = useState(asset.Subject ?? '');
    const [templateAssetId, setTemplateAssetId] = useState(asset.EmailTemplateAssetId ?? null);
    const [templates, setTemplates] = useState([]);
    const [templatesLoading, setTemplatesLoading] = useState(false);
    const [saveStatus, setSaveStatus] = useState('idle');
    const [saveError, setSaveError] = useState(null);
    // Refs capture the latest in-flight values so the save closure reads them at fire
    // time rather than at construction time.
    const subjectRef = useRef(subject);
    const templateRef = useRef(templateAssetId);
    subjectRef.current = subject;
    templateRef.current = templateAssetId;
    // Pending refs hold the values awaiting persistence; the lastPersisted refs let us
    // tell our own save's refetch from a server-pushed change so we don't remount the
    // Unlayer canvas on every save round-trip.
    const pendingDesignRef = useRef(null);
    const pendingHtmlRef = useRef(null);
    const lastPersistedDesignRef = useRef(asset.DraftDesign ?? null);
    const lastPersistedHtmlRef = useRef(asset.DraftHtml ?? null);
    const designSaveTimerRef = useRef(null);
    const designerRef = useRef(null);
    // Tracks which save path most recently failed so handleRetry can re-run the right one.
    // Pending refs alone aren't reliable: after a successful design save they stay non-null
    // (they're also used for the still-matches check), which would make a Retry after a
    // subject/template failure wrongly route to the no-op design save.
    const lastFailedPathRef = useRef(null);
    // Tracks what we last persisted across all fields so the external-change effect can
    // tell our own save's refetch (no-op) from a server-pushed change (e.g., Webster
    // rebuild, or a re-clone after a template re-bind) that should remount the editor.
    const lastPersistedRef = useRef({
        subject: asset.Subject ?? '',
        template: asset.EmailTemplateAssetId ?? null,
    });
    const [designerKey, setDesignerKey] = useState(0);
    useEffect(() => {
        const incoming = {
            subject: asset.Subject ?? '',
            template: asset.EmailTemplateAssetId ?? null,
        };
        const persisted = lastPersistedRef.current;
        const subjectChanged = incoming.subject !== persisted.subject;
        const templateChanged = incoming.template !== persisted.template;
        // Content equality, not referential. After our own save lands and the parent
        // refetches, asset.DraftDesign is a brand-new object parsed from the response
        // with identical content — referential `!==` would falsely report it as an
        // external change and remount the canvas. JSON-stringify is cheap and reliable
        // because the design is JSON-serialisable by definition (it's Unlayer's wire format).
        const designChanged = serializeForCompare(asset.DraftDesign ?? null) !== serializeForCompare(lastPersistedDesignRef.current);
        const htmlChanged = (asset.DraftHtml ?? null) !== lastPersistedHtmlRef.current;
        if (!subjectChanged && !templateChanged && !designChanged && !htmlChanged)
            return;
        if (subjectChanged)
            setSubject(incoming.subject);
        if (templateChanged)
            setTemplateAssetId(incoming.template);
        if (designChanged || htmlChanged) {
            // Remount UnlayerDesigner so it picks up the new initialDesign — Unlayer's
            // iframe doesn't expose a way to swap designs without remount.
            lastPersistedDesignRef.current = asset.DraftDesign ?? null;
            lastPersistedHtmlRef.current = asset.DraftHtml ?? null;
            pendingDesignRef.current = null;
            pendingHtmlRef.current = null;
            if (designSaveTimerRef.current)
                clearTimeout(designSaveTimerRef.current);
            setDesignerKey(k => k + 1);
        }
        lastPersistedRef.current = incoming;
    }, [asset.Subject, asset.EmailTemplateAssetId, asset.DraftDesign, asset.DraftHtml]);
    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            setTemplatesLoading(true);
            const response = await assetApi.getEmailTemplatesByWeb(clientCompanyId, workspaceId, campaignId, webId);
            if (cancelled)
                return;
            if (response.data)
                setTemplates(response.data);
            setTemplatesLoading(false);
        };
        void load();
        return () => { cancelled = true; };
    }, [clientCompanyId, workspaceId, campaignId, webId]);
    const runSubjectTemplateSave = useCallback(async () => {
        setSaveStatus('saving');
        setSaveError(null);
        const snapshot = {
            subject: subjectRef.current,
            template: templateRef.current,
        };
        const response = await assetApi.updateAsset(clientCompanyId, workspaceId, campaignId, asset.Id, {
            Id: asset.Id,
            CampaignId: asset.CampaignId,
            Subject: snapshot.subject,
            EmailTemplateAssetId: snapshot.template ?? '',
        });
        if (response.data) {
            lastPersistedRef.current = snapshot;
            lastFailedPathRef.current = null;
            const stillMatches = subjectRef.current === snapshot.subject &&
                templateRef.current === snapshot.template;
            setSaveStatus(stillMatches ? 'saved' : 'dirty');
            await onSaved?.();
        }
        else {
            lastFailedPathRef.current = 'subjectTemplate';
            setSaveStatus('error');
            setSaveError(response.error ?? 'Save failed.');
        }
    }, [clientCompanyId, workspaceId, campaignId, asset.Id, asset.CampaignId, onSaved]);
    const runDesignSave = useCallback(async () => {
        const designSnapshot = pendingDesignRef.current;
        const htmlSnapshot = pendingHtmlRef.current;
        const designChanged = designSnapshot != null && designSnapshot !== lastPersistedDesignRef.current;
        const htmlChanged = htmlSnapshot != null && htmlSnapshot !== lastPersistedHtmlRef.current;
        if (!designChanged && !htmlChanged)
            return;
        setSaveStatus('saving');
        setSaveError(null);
        const response = await assetApi.updateAsset(clientCompanyId, workspaceId, campaignId, asset.Id, {
            Id: asset.Id,
            CampaignId: asset.CampaignId,
            ...(designChanged && designSnapshot != null ? { DraftDesign: designSnapshot } : {}),
            ...(htmlChanged && htmlSnapshot != null ? { DraftHtml: htmlSnapshot } : {}),
        });
        if (response.data) {
            if (designSnapshot != null)
                lastPersistedDesignRef.current = designSnapshot;
            if (htmlSnapshot != null)
                lastPersistedHtmlRef.current = htmlSnapshot;
            lastFailedPathRef.current = null;
            const stillMatches = pendingDesignRef.current === designSnapshot &&
                pendingHtmlRef.current === htmlSnapshot;
            setSaveStatus(stillMatches ? 'saved' : 'dirty');
            await onSaved?.();
        }
        else {
            lastFailedPathRef.current = 'design';
            setSaveStatus('error');
            setSaveError(response.error ?? 'Save failed.');
        }
    }, [clientCompanyId, workspaceId, campaignId, asset.Id, asset.CampaignId, onSaved]);
    const handleDesignUpdate = useCallback((design, html) => {
        pendingDesignRef.current = design;
        pendingHtmlRef.current = html;
        const designChanged = design !== lastPersistedDesignRef.current;
        const htmlChanged = html !== lastPersistedHtmlRef.current;
        if (!designChanged && !htmlChanged)
            return;
        setSaveStatus('dirty');
        if (designSaveTimerRef.current)
            clearTimeout(designSaveTimerRef.current);
        designSaveTimerRef.current = setTimeout(() => { void runDesignSave(); }, 800);
    }, [runDesignSave]);
    useEffect(() => () => {
        if (designSaveTimerRef.current)
            clearTimeout(designSaveTimerRef.current);
    }, []);
    // Imperative handle for promote-to-version. Pulls the current design + HTML straight
    // from Unlayer (so a freshly cloned copy whose DraftHtml is null still gets compiled),
    // populates the pending refs, cancels any in-flight debounce, and runs the save. The
    // server-side promote that fires after this resolves now finds a populated DraftHtml
    // instead of failing the "Body Required" validation.
    useImperativeHandle(ref, () => ({
        flushAndSave: async () => {
            if (!designerRef.current)
                return;
            const design = await designerRef.current.getDesign();
            const html = await designerRef.current.getHtml();
            pendingDesignRef.current = design;
            pendingHtmlRef.current = html;
            if (designSaveTimerRef.current) {
                clearTimeout(designSaveTimerRef.current);
                designSaveTimerRef.current = null;
            }
            await runDesignSave();
        },
    }), [runDesignSave]);
    const handleSubjectBlur = useCallback(() => {
        if (subjectRef.current !== lastPersistedRef.current.subject) {
            void runSubjectTemplateSave();
        }
    }, [runSubjectTemplateSave]);
    const handleTemplateChange = useCallback((e) => {
        const value = e.target.value || null;
        setTemplateAssetId(value);
        templateRef.current = value;
        if (value !== lastPersistedRef.current.template) {
            void runSubjectTemplateSave();
        }
    }, [runSubjectTemplateSave]);
    const handleRetry = useCallback(() => {
        // Route to whichever save path most recently failed. Falls back to design save
        // if nothing's recorded (e.g., manual retry after a transient hiccup with no
        // prior error tracked) since that's the more frequent path.
        if (lastFailedPathRef.current === 'subjectTemplate') {
            void runSubjectTemplateSave();
        }
        else {
            void runDesignSave();
        }
    }, [runDesignSave, runSubjectTemplateSave]);
    const templateName = templates.find(t => t.Id === templateAssetId)?.DisplayName;
    return (_jsxs("div", { className: "flex flex-col h-full bg-white dark:bg-slate-900", children: [_jsxs("div", { className: "flex-shrink-0 px-4 py-3 border-b border-slate-200 dark:border-slate-700 space-y-3 bg-slate-50/50 dark:bg-slate-800/30", children: [_jsx(TextBox, { name: "subject", label: "Subject", value: subject, placeholder: "Email subject line", disabled: !editable, onChange: (next) => { setSubject(next); setSaveStatus('dirty'); }, onBlur: handleSubjectBlur }), templates.length === 0 && !templatesLoading ? (_jsxs("div", { children: [_jsx("label", { className: `block text-sm font-medium mb-1 ${BRAND_CLASSES.INPUT.LABEL}`, children: "Template" }), _jsxs("div", { className: "flex items-center gap-2 px-3 py-2 text-xs rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-300", children: [_jsx(Mail, { className: "w-3.5 h-3.5" }), _jsx("span", { children: "No templates yet \u2014 create one in the Email Template editor for this campaign." })] })] })) : (_jsx(Select, { name: "template", label: templatesLoading ? 'Template (loading…)' : 'Template', placeholder: "\u2014 Select a template \u2014", value: templateAssetId ?? '', disabled: !editable || templatesLoading, options: templates.map(t => ({ value: t.Id, label: t.DisplayName })), onChange: handleTemplateChange }))] }), _jsx("div", { className: "flex-1 min-h-0", children: templateAssetId && asset.DraftDesign ? (_jsx(UnlayerDesigner, { ref: designerRef, initialDesign: asset.DraftDesign, editable: editable, onUpdate: handleDesignUpdate, mergeTags: COPY_MERGE_TAGS, className: "h-full" }, designerKey)) : (_jsx("div", { className: "h-full flex items-center justify-center p-6", children: _jsxs("div", { className: "max-w-sm text-center text-sm text-slate-500 dark:text-slate-400 space-y-2", children: [_jsx("p", { className: "font-medium text-slate-700 dark:text-slate-200", children: !templateAssetId ? 'Pick a template to start' : 'Loading editor…' }), _jsx("p", { children: !templateAssetId
                                    ? 'Your email\'s design will be created from the template you select above.'
                                    : 'Your design is being prepared from the bound template.' })] }) })) }), _jsxs("div", { className: "flex-shrink-0 px-4 py-3 border-t border-slate-200 dark:border-slate-700 flex items-center justify-between gap-3 bg-slate-50/50 dark:bg-slate-800/30", children: [_jsx("div", { className: `text-[11px] truncate ${BRAND_CLASSES.INPUT.HELPER}`, children: templateName ? `Bound to "${templateName}"` : 'No template bound yet' }), _jsxs("div", { className: "flex items-center gap-2", children: [templateAssetId && (_jsx(ResetCopyToTemplateButton, { clientCompanyId: clientCompanyId, workspaceId: workspaceId, campaignId: campaignId, assetId: asset.Id, disabled: !editable, onReset: onSaved })), _jsx(SaveStatusIndicator, { status: saveStatus, error: saveError, onRetry: handleRetry })] })] })] }));
});
